import { useQuery } from '@apollo/react-hooks'
import { ApolloClient, gql } from 'apollo-boost'
import { RestaurantsAPI5ClientCtxx } from 'context'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { EditOrderAction, EditOrderModifier, useEditOrderSvc } from '../service'
import Autosuggest from 'react-autosuggest'
import { LoadingModal } from '../components/loading'

interface MenuItemDetails {
  item: {
    id: number
    sizes: {
      id: number
      modifiers: MenuItemModifier[]
    }[]
  }
}

interface MenuItemModifier {
  id: number
  name: string
  options: MenuItemOption[]
}

interface MenuItemOption {
  id: number
  name: string
  price: number
}

interface AddModifierProps {
  cartItemId: number
  itemName: string
  onClose: () => void
  onSave: (result: EditOrderAction) => void
}

interface SuggestOption {
  id: number
  name: string
  price: number
  modifier: MenuItemModifier
}

const EDITOR_NAME = 'Add Modifier Option'

export default function AddModifier(props: AddModifierProps): JSX.Element {
  const editOrderSvc = useEditOrderSvc()
  const restaurantId = editOrderSvc.getRestaurantId()
  const item = useMemo(() => editOrderSvc.getItem(props.cartItemId), [])

  const restaurantsClient = (useContext(RestaurantsAPI5ClientCtxx) as unknown) as ApolloClient<unknown>

  const itemQuery = useQuery<MenuItemDetails>(GET_MENU_ITEM, {
    variables: { item_id: item.item_id, restaurant_id: restaurantId },
    client: restaurantsClient,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  if (itemQuery.data) {
    const menuItemSize = itemQuery.data.item.sizes.find(({ id }) => id === item.item_id)
    const modifiers = menuItemSize ? menuItemSize.modifiers : []
    const options: SuggestOption[] = modifiers.reduce(
      (sum, m) => m.options.reduce((sum, o) => [...sum, { ...o, modifier: m }], sum),
      []
    )
    return <AddModifierUI {...props} options={options} />
  } else {
    return <LoadingModal title={EDITOR_NAME} onClose={props.onClose} />
  }
}

interface EditOrderFormModifier {
  option: SuggestOption
  price: number
  quantity: number
}

function AddModifierUI(props: AddModifierProps & { options: SuggestOption[] }): JSX.Element {
  const form = useForm<EditOrderFormModifier>({
    mode: 'onBlur',
    defaultValues: {
      price: 0,
      quantity: 0,
    },
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    control,
  } = form

  const option = watch('option')

  useEffect(() => {
    if (option) {
      setValue('price', option.price)
    }
  }, [option])

  const validateAndSave = (data: EditOrderFormModifier): void => {
    const payload = {
      id: data.option.id,
      name: data.option.name,
      price: data.price,
      quantity: data.quantity,
      modifier_name: data.option.modifier.name,
    } as EditOrderModifier
    props.onSave({ op: 'add-ingredient', item_id: props.cartItemId, ingredient: payload, item_name: props.itemName })
    props.onClose()
  }

  const renderOption = (o: SuggestOption): JSX.Element => {
    return (
      <div>
        <div key="name">
          <strong>{o.name}</strong>
        </div>
        <div key="path" className="text-muted">
          {o.modifier.name}
        </div>
      </div>
    )
  }

  const [suggestions, setSuggestions] = useState<SuggestOption[]>(props.options)
  const [autocompleteValue, onChangeAutocompleteValue] = useState('')

  return (
    <Modal isOpen={true} onClosed={props.onClose}>
      <ModalHeader>
        <span>
          {EDITOR_NAME} to {props.itemName}
        </span>
      </ModalHeader>
      <form className="form-horizontal" onSubmit={handleSubmit(validateAndSave)}>
        <ModalBody>
          <FormProvider {...form}>
            <div className="form-group">
              <label htmlFor={'option'}>Modifier Name:</label>
              <Controller
                control={control}
                name={'option'}
                rules={{ required: { value: true, message: 'Please select a modifier option' } }}
                render={({ field: { onChange, onBlur, name, ref } }) => (
                  <Autosuggest
                    suggestions={suggestions}
                    renderSuggestion={renderOption}
                    onSuggestionSelected={(_event: any, data: any) => {
                      onChange(data.suggestion)
                    }}
                    onSuggestionsFetchRequested={({ value }: { value: string }) => {
                      setSuggestions(
                        props.options.filter(({ name }) => name.toLowerCase().startsWith(value.toLowerCase()))
                      )
                    }}
                    onSuggestionsClearRequested={() => setSuggestions([])}
                    getSuggestionValue={(i: SuggestOption) => i.name}
                    inputProps={{
                      name,
                      onBlur,
                      ref,
                      value: autocompleteValue,
                      placeholder: 'search items or enter a new item name',
                      onChange: (_: any, { newValue }: { newValue: string }) => {
                        onChangeAutocompleteValue(newValue)
                      },
                    }}
                  />
                )}
              />
              {errors && errors['option'] && <span className="text-danger">{errors['option'].message}</span>}
            </div>
          </FormProvider>
          <div className="form-group">
            <label htmlFor="price">Price:</label>
            <input
              id="price"
              className="form-control input input-small"
              style={errors && errors.price ? { color: 'red' } : {}}
              type="number"
              step={0.01}
              {...register('price', {
                required: 'is required',
                min: { value: 0.01, message: 'must be greater than 0' },
                valueAsNumber: true,
              })}
            />
            {errors && errors.price && <span className="text-danger">{errors.price.message}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="quantity">Quantity:</label>
            <input
              id="quantity"
              className="form-control input input-small"
              style={errors && errors.quantity ? { color: 'red' } : {}}
              type="number"
              step={1}
              {...register('quantity', {
                required: 'is required',
                min: { value: 1, message: 'must be greater than 0' },
                valueAsNumber: true,
              })}
            />
            {errors && errors.quantity && <span className="text-danger">{errors.quantity.message}</span>}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="submit" color="primary">
            Save
          </Button>
          <Button color="secondary" onClick={props.onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

const GET_MENU_ITEM = gql`
  query LoadItem($item_id: Int!, $restaurant_id: Int!) {
    item(itemId: $item_id, restaurantId: $restaurant_id) {
      sizes {
        id
        modifiers {
          id
          name
          options {
            id
            name
            price
          }
        }
      }
    }
  }
`
